/**
 * `/admin/users`.
 *
 * **Roles, email and `entra_object_id` are read-only and visibly so** (§2 rule
 * 4). They are written only by JIT provisioning from the token claims on every
 * request; role assignment happens in Entra and nowhere else. The API rejects
 * them by name rather than stripping them, and this screen shows them greyed
 * with the reason, because an Admin who believes they granted a role and did
 * not is worse off than one who got an error.
 *
 * **Deactivation offers the reassignment flow rather than failing.** §8c
 * refuses to deactivate a user who owns open records unless a target is
 * supplied — so the screen asks for the target and calls `/reassign` first,
 * which moves opportunity ownership, account ownership, `fae_user_id` and open
 * action items in one audited transaction.
 *
 * A caveat worth knowing while using this screen: setting `is_active = false`
 * takes effect on that user's **next request**, because it is checked
 * server-side every time. Disabling their Entra account is the durable step,
 * but an already-issued access token keeps working until it expires — 68
 * minutes at most, measured (§8c [R6]). Nothing shortens that, which is why
 * this screen is the control rather than a convenience.
 */

import { useState } from 'react';
import { EMAIL_REMINDER_MODES } from '@menlo/shared';
import { useApiMutation, useApiQuery } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { api } from '@/lib/api';
import { formatDateTime, humanize } from '@/lib/format';

interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
  title: string | null;
  timezone: string;
  is_active: boolean;
  email_reminders: string;
  manager_user_id: string | null;
  last_login_at: string | null;
}

const USERS_KEY = ['admin', 'users'] as const;

export function AdminUsers(): React.ReactElement {
  const users = useApiQuery<{ items: AdminUser[] }>(USERS_KEY, '/api/admin/users');
  const [deactivating, setDeactivating] = useState<AdminUser | null>(null);

  const update = useApiMutation<
    Record<string, unknown>,
    { id: string; body: Record<string, unknown> }
  >(({ id, body }) => ({ path: `/api/admin/users/${id}`, method: 'PUT', body }), {
    invalidate: [USERS_KEY],
    quiet: true,
  });

  const { push } = useToast();
  const items = users.data?.items ?? [];

  const setActive = async (user: AdminUser, active: boolean): Promise<void> => {
    if (!active) {
      try {
        await api(`/api/admin/users/${user.id}`, {
          method: 'PUT',
          body: { is_active: false },
        });
        void users.refetch();
      } catch (error) {
        if (error instanceof ApiError && error.code === 'REASSIGNMENT_REQUIRED') {
          setDeactivating(user);
        } else {
          push({
            tone: 'error',
            title: error instanceof ApiError ? error.message : 'Could not deactivate.',
          });
        }
      }
      return;
    }
    update.mutate({ id: user.id, body: { is_active: true } });
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Identity and roles come from the Entra token on every request. Deactivating here takes
        effect on that person&rsquo;s next request; disabling their Entra account is the durable
        step.
      </p>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-2 py-1.5">
                Name
              </th>
              <th scope="col" className="px-2 py-1.5">
                Email
              </th>
              <th scope="col" className="px-2 py-1.5">
                Roles
              </th>
              <th scope="col" className="px-2 py-1.5">
                Title
              </th>
              <th scope="col" className="px-2 py-1.5">
                Manager
              </th>
              <th scope="col" className="px-2 py-1.5">
                Timezone
              </th>
              <th scope="col" className="px-2 py-1.5">
                Reminders
              </th>
              <th scope="col" className="px-2 py-1.5">
                Active
              </th>
              <th scope="col" className="px-2 py-1.5">
                Last seen
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((user) => (
              <tr key={user.id} className="border-t">
                <td className="px-2 py-1.5 font-medium">{user.display_name}</td>
                <td
                  className="px-2 py-1.5 text-xs text-muted-foreground"
                  title="From the token. Not editable here (§2 rule 4)."
                >
                  {user.email}
                </td>
                <td className="px-2 py-1.5">
                  <span className="flex flex-wrap gap-1" title="Assigned in Entra, cached here.">
                    {user.roles.map((role) => (
                      <Badge key={role} variant="outline">
                        {role}
                      </Badge>
                    ))}
                    {user.roles.length === 0 && (
                      <span className="text-xs text-muted-foreground">none</span>
                    )}
                  </span>
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    aria-label={`Title for ${user.display_name}`}
                    className="h-7 w-36 text-xs"
                    defaultValue={user.title ?? ''}
                    onBlur={(event) =>
                      update.mutate({
                        id: user.id,
                        body: { title: event.target.value === '' ? null : event.target.value },
                      })
                    }
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Select
                    aria-label={`Manager for ${user.display_name}`}
                    className="h-7 w-36 text-xs"
                    defaultValue={user.manager_user_id ?? ''}
                    onChange={(event) =>
                      update.mutate({
                        id: user.id,
                        body: {
                          manager_user_id: event.target.value === '' ? null : event.target.value,
                        },
                      })
                    }
                  >
                    <option value="">—</option>
                    {items
                      .filter((candidate) => candidate.id !== user.id)
                      .map((candidate) => (
                        <option key={candidate.id} value={candidate.id}>
                          {candidate.display_name}
                        </option>
                      ))}
                  </Select>
                </td>
                <td className="px-2 py-1.5">
                  <Input
                    aria-label={`Timezone for ${user.display_name}`}
                    className="h-7 w-40 text-xs"
                    defaultValue={user.timezone}
                    onBlur={(event) =>
                      update.mutate({ id: user.id, body: { timezone: event.target.value } })
                    }
                  />
                </td>
                <td className="px-2 py-1.5">
                  <Select
                    aria-label={`Reminders for ${user.display_name}`}
                    className="h-7 w-28 text-xs"
                    defaultValue={user.email_reminders}
                    onChange={(event) =>
                      update.mutate({
                        id: user.id,
                        body: { email_reminders: event.target.value },
                      })
                    }
                  >
                    {EMAIL_REMINDER_MODES.map((value) => (
                      <option key={value} value={value}>
                        {humanize(value)}
                      </option>
                    ))}
                  </Select>
                </td>
                <td className="px-2 py-1.5">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5"
                      checked={user.is_active}
                      onChange={(event) => void setActive(user, event.target.checked)}
                    />
                    {user.is_active ? 'Active' : 'Off'}
                  </label>
                </td>
                <td className="px-2 py-1.5 text-xs text-muted-foreground">
                  {user.last_login_at === null ? 'never' : formatDateTime(user.last_login_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deactivating !== null && (
        <ReassignDialog
          user={deactivating}
          candidates={items.filter(
            (candidate) => candidate.id !== deactivating.id && candidate.is_active,
          )}
          onClose={() => setDeactivating(null)}
          onDone={() => {
            setDeactivating(null);
            void users.refetch();
          }}
        />
      )}
    </div>
  );
}

function ReassignDialog({
  user,
  candidates,
  onClose,
  onDone,
}: {
  user: AdminUser;
  candidates: AdminUser[];
  onClose: () => void;
  onDone: () => void;
}): React.ReactElement {
  const { push } = useToast();
  const [toUserId, setToUserId] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (): Promise<void> => {
    setBusy(true);
    try {
      const moved = await api<{ moved: Record<string, number> }>(
        `/api/admin/users/${user.id}/reassign`,
        { method: 'POST', body: { to_user_id: toUserId, note: 'Offboarding' } },
      );
      await api(`/api/admin/users/${user.id}`, { method: 'PUT', body: { is_active: false } });
      push({
        tone: 'success',
        title: `${user.display_name} deactivated`,
        body: Object.entries(moved.moved)
          .map(([what, count]) => `${count} ${humanize(what).toLowerCase()}`)
          .join(', '),
      });
      onDone();
    } catch (error) {
      push({
        tone: 'error',
        title: error instanceof ApiError ? error.message : 'The reassignment failed.',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={`Reassign ${user.display_name}'s work`}
      description="They still own open records, so deactivation needs somewhere for that work to go."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void run()} disabled={toUserId === '' || busy}>
            Reassign and deactivate
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Move everything to">
          <Select value={toUserId} onChange={(event) => setToUserId(event.target.value)}>
            <option value="">Choose someone…</option>
            {candidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.display_name}
              </option>
            ))}
          </Select>
        </Field>
        <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
          <li>Opportunities they own, and ones where they are the assigned FAE.</li>
          <li>Accounts they own.</li>
          <li>Open action items.</li>
          <li>One audited transaction. Closed and cancelled work is left where it is.</li>
        </ul>
      </div>
    </Dialog>
  );
}
